import React, { useState, useContext, useEffect, useRef } from 'react';
import { UserContext } from '../userContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  query,
  where,
  getDoc,
  setDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { updateProfile, updatePassword } from 'firebase/auth';
import { auth, db, storage } from '../firebaseConfig';
import {
  FaUser,
  FaUpload,
  FaHistory,
  FaSave,
  FaPlus,
  FaTrash,
  FaEdit,
  FaEye,
  FaFilter,
  FaTimes,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaFile,
  FaQuestion,
  FaCalculator,
  FaVideo,
  FaImage,
  FaFilePdf,
  FaLink,
  FaCalendarAlt,
  FaGraduationCap,
  FaCog
} from 'react-icons/fa';
import './Perfil.css';

/* -------------------------------------------------------------------------------------------------
   Utilitários PDF.js + crop
--------------------------------------------------------------------------------------------------*/
// Carrega PDF.js sob demanda uma única vez
async function ensurePdfJsLoaded() {
  if (window.pdfjsLib) return;
  await new Promise((resolve, reject) => {
    const s1 = document.createElement('script');
    s1.src =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s1.onload = resolve;
    s1.onerror = reject;
    document.head.appendChild(s1);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
}

// Renderiza a página 1 do PDF num canvas (para recorte)
async function renderPdfFirstPageToCanvas(fileOrUrl, maxW = 1200) {
  await ensurePdfJsLoaded();
  const loadingTask =
    typeof fileOrUrl === 'string'
      ? window.pdfjsLib.getDocument(fileOrUrl)
      : window.pdfjsLib.getDocument({ data: await fileOrUrl.arrayBuffer() });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);

  const vp = page.getViewport({ scale: 1 });
  const scale = Math.min(maxW / vp.width, 1600 / vp.height); // limita tamanho
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext('2d');

  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

function blobFromCanvas(canvas, type = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/* -------------------------------------------------------------------------------------------------
   Modal de recorte 16:9
--------------------------------------------------------------------------------------------------*/
function PdfCropperModal({
  open,
  onClose,
  sourceCanvas, // canvas com a página 1 já renderizada
  onConfirm, // (blob, dataUrl) => void
  lock16x9Default = true,
}) {
  const wrapperRef = useRef(null);
  const overlayRef = useRef(null);
  const [lock169, setLock169] = useState(lock16x9Default);
  const [imgUrl, setImgUrl] = useState('');
  const [rect, setRect] = useState(null); // {x,y,w,h}
  const dragState = useRef(null);

  useEffect(() => {
    if (!open) return;
    const url = sourceCanvas.toDataURL('image/jpeg', 0.92);
    setImgUrl(url);

    // retângulo inicial central em 16:9
    const w = sourceCanvas.width;
    const h = sourceCanvas.height;
    const baseW = Math.min(w * 0.9, h * (16 / 9));
    const baseH = baseW / (16 / 9);
    setRect({
      x: Math.round((w - baseW) / 2),
      y: Math.round((h - baseH) / 2),
      w: Math.round(baseW),
      h: Math.round(baseH),
    });
  }, [open, sourceCanvas]);

  if (!open) return null;

  // Converte clique do usuário (na imagem escalada) para coords da imagem original
  const toImageCoords = (e) => {
    const img = wrapperRef.current.querySelector('img');
    const bounds = img.getBoundingClientRect();
    const scaleX = sourceCanvas.width / bounds.width;
    const scaleY = sourceCanvas.height / bounds.height;
    const x = (e.clientX - bounds.left) * scaleX;
    const y = (e.clientY - bounds.top) * scaleY;
    return { x, y, scaleX, scaleY, bounds };
  };

  const startDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = toImageCoords(e);
    dragState.current = { mode, startX: x, startY: y, startRect: { ...rect } };
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', endDrag);
  };

  const onDrag = (e) => {
    if (!dragState.current) return;
    const { x, y } = toImageCoords(e);
    const { mode, startX, startY, startRect } = dragState.current;

    let nx = startRect.x;
    let ny = startRect.y;
    let nw = startRect.w;
    let nh = startRect.h;

    const dx = x - startX;
    const dy = y - startY;

    if (mode === 'move') {
      nx = Math.max(0, Math.min(sourceCanvas.width - nw, startRect.x + dx));
      ny = Math.max(0, Math.min(sourceCanvas.height - nh, startRect.y + dy));
    } else {
      // resize corners (modo: 'nw','ne','sw','se')
      const sx = mode.includes('w') ? -1 : 1;
      const sy = mode.includes('n') ? -1 : 1;
      let newW = startRect.w + sx * dx;
      let newH = startRect.h + sy * dy;

      if (lock169) {
        const ratio = 16 / 9;
        if (Math.abs(dx) > Math.abs(dy)) {
          newH = newW / ratio;
        } else {
          newW = newH * ratio;
        }
      }
      // limites mínimos
      newW = Math.max(80, newW);
      newH = Math.max(45, newH);

      // recalcula x/y conforme a âncora
      nx = mode.includes('w') ? startRect.x + (startRect.w - newW) : startRect.x;
      ny = mode.includes('n') ? startRect.y + (startRect.h - newH) : startRect.y;

      // clamp dentro da imagem
      if (nx < 0) { newW += nx; nx = 0; }
      if (ny < 0) { newH += ny; ny = 0; }
      if (nx + newW > sourceCanvas.width) newW = sourceCanvas.width - nx;
      if (ny + newH > sourceCanvas.height) newH = sourceCanvas.height - ny;

      nw = newW; nh = newH;
    }

    setRect({ x: Math.round(nx), y: Math.round(ny), w: Math.round(nw), h: Math.round(nh) });
  };

  const endDrag = () => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', endDrag);
    dragState.current = null;
  };

  const confirmCrop = async () => {
    const out = document.createElement('canvas');
    out.width = 1280;
    out.height = 720; // 16:9
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, out.width, out.height);

    // desenha recorte no canvas de saída mantendo 16:9
    ctx.drawImage(
      sourceCanvas,
      rect.x, rect.y, rect.w, rect.h,
      0, 0, out.width, out.height
    );

    const blob = await blobFromCanvas(out, 'image/jpeg', 0.9);
    const dataUrl = out.toDataURL('image/jpeg', 0.9);
    onConfirm(blob, dataUrl);
  };

  // estilos inline (não mexe no seu CSS)
  const modalStyles = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    },
    content: {
      width: 'min(96vw, 1000px)', maxHeight: '90vh', overflow: 'auto',
      background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 10px 30px rgba(0,0,0,.2)'
    },
    title: { fontSize: 22, marginBottom: 4, textAlign: 'center' },
    subtitle: { fontSize: 14, color: '#6b7280', textAlign: 'center', marginBottom: 14 },
    row: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
    imgWrap: {
      position: 'relative', margin: '0 auto', maxWidth: '100%', userSelect: 'none',
      borderRadius: 8, overflow: 'hidden', boxShadow: '0 0 0 1px #e5e7eb inset'
    },
    img: { display: 'block', width: '100%', height: 'auto' },
    cropBox: (r, scale) => ({
      position: 'absolute',
      left: `${r.x / scale}px`,
      top: `${r.y / scale}px`,
      width: `${r.w / scale}px`,
      height: `${r.h / scale}px`,
      border: '2px solid #209cd3',
      boxShadow: '0 0 0 9999px rgba(0,0,0,.35)',
      cursor: 'move'
    }),
    handle: (pos) => ({
      position: 'absolute', width: 14, height: 14, borderRadius: 14,
      background: '#209cd3', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
      cursor: `${pos}-resize`,
      ...(pos.includes('n') ? { top: -7 } : { bottom: -7 }),
      ...(pos.includes('w') ? { left: -7 } : { right: -7 })
    }),
    actions: { display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }
  };

  // escala atual da imagem na tela
  let scale = 1;
  if (wrapperRef.current) {
    const img = wrapperRef.current.querySelector('img');
    if (img) scale = sourceCanvas.width / img.getBoundingClientRect().width;
  }

  return (
    <div ref={overlayRef} style={modalStyles.overlay} onMouseUp={endDrag}>
      <div style={modalStyles.content}>
        <h3 style={modalStyles.title}>Ajustar preview do PDF</h3>
        <p style={modalStyles.subtitle}>Arraste o retângulo para definir a área. Proporção 16:9 {lock169 ? 'travada' : 'liberada'}.</p>

        <div style={{ ...modalStyles.row, justifyContent: 'flex-start' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={lock169}
              onChange={() => setLock169(v => !v)}
            />
            Travar em 16:9
          </label>
        </div>

        <div ref={wrapperRef} style={modalStyles.imgWrap}>
          <img src={imgUrl} alt="Prévia do PDF" style={modalStyles.img} />
          {rect && (
            <div
              style={modalStyles.cropBox(rect, scale)}
              onMouseDown={startDrag('move')}
            >
              {/* handles */}
              <div style={modalStyles.handle('nw')} onMouseDown={startDrag('nw')} />
              <div style={modalStyles.handle('ne')} onMouseDown={startDrag('ne')} />
              <div style={modalStyles.handle('sw')} onMouseDown={startDrag('sw')} />
              <div style={modalStyles.handle('se')} onMouseDown={startDrag('se')} />
            </div>
          )}
        </div>

        <div style={modalStyles.actions}>
          <button
            onClick={confirmCrop}
            style={{ padding: '10px 16px', background: '#209cd3', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            Confirmar recorte
          </button>
          <button
            onClick={onClose}
            style={{ padding: '10px 16px', background: '#e5e7eb', color: '#111827', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------------------------------
   Componente principal
--------------------------------------------------------------------------------------------------*/
function Perfil() {
  const { user, setUser } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Pegar aba da URL
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab') || 'dados';
  const [abaAtiva, setAbaAtiva] = useState(tabFromUrl);

  // Meus Dados
  const [dadosPerfil, setDadosPerfil] = useState({
    nome: user?.name || '',
    email: user?.email || '',
    telefone: user?.telefone || '',
    bio: user?.bio || '',
    novaSenha: '',
    confirmarSenha: ''
  });
  const [fotoFile, setFotoFile] = useState(null);
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [mensagemDados, setMensagemDados] = useState('');

  // Enviar Material/Pergunta (FORMULÁRIO ATUALIZADO)
  const [categorias, setCategorias] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [novoMaterial, setNovoMaterial] = useState({
    // Informações já preenchidas
    nome: user?.name || '',
    email: user?.email || '',

    // Detalhes da solicitação
    tipoSolicitacao: '', // material | questoes
    descricao: '',

    // Contexto acadêmico
    semestre: '',
    curso: '',

    // Detalhes da questão
    arquivo: null,
    linkRelacionado: '',
    urgencia: 'normal', // normal | urgente | muito_urgente

    // Preferências
    formatoResposta: [], // Array de preferências: videoaula, material_escrito, resumo, exercicio_resolvido

    // Campos legados (para compatibilidade)
    titulo: '',
    tipo: '', // será mapeado do tipoSolicitacao
    tipoMaterial: 'arquivo',
    categoria: '',
    subcategoria: '',
    conteudo: '',
    videoUrl: '',
    materialType: 'TEXT'
  });


  const [categorySelections, setCategorySelections] = useState([
    { category: '', subcategory: '' }
  ]);

  // Funções para gerenciar múltiplas categorias
  const addCategorySelection = () => {
    setCategorySelections([...categorySelections, { category: '', subcategory: '' }]);
  };

  const removeCategorySelection = (index) => {
    if (categorySelections.length > 1) {
      const newSelections = categorySelections.filter((_, i) => i !== index);
      setCategorySelections(newSelections);
    }
  };

  const updateCategorySelection = (index, field, value) => {
    const newSelections = [...categorySelections];
    newSelections[index] = {
      ...newSelections[index],
      [field]: value,
      // Reset subcategory quando muda categoria
      ...(field === 'category' && { subcategory: '' })
    };
    setCategorySelections(newSelections);
  };

  const getSubcategoryOptions = (catId) => {
    const cat = categorias.find((c) => c.id === catId);
    return cat ? cat.subcategories : [];
  };


  const [enviandoMaterial, setEnviandoMaterial] = useState(false);
  const [mensagemEnvio, setMensagemEnvio] = useState('');
  const [toastObrigatorios, setToastObrigatorios] = useState('');

  // Meus Envios
  const [meusEnvios, setMeusEnvios] = useState([]);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroMes, setFiltroMes] = useState('');
  const [carregandoEnvios, setCarregandoEnvios] = useState(false);

  // Edição de envio
  const [editandoEnvio, setEditandoEnvio] = useState(null);
  const [dadosEdicao, setDadosEdicao] = useState({});
  const [arquivoEdicao, setArquivoEdicao] = useState(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [mensagemEdicao, setMensagemEdicao] = useState('');

  // PDF Cropper (envio novo)
  const [cropOpen, setCropOpen] = useState(false);
  const [cropCanvas, setCropCanvas] = useState(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState('');
  const cropBlobRef = useRef(null); // blob do recorte escolhido

  const firstLetter = user?.name?.charAt(0).toUpperCase() || 'U';
  const podeEnviarMaterial = !!user?.uid;

  // Dados estáticos para disciplinas, cursos, etc.
  const disciplinasDisponiveis = [
    'Matemática', 'Física', 'Química', 'Biologia', 'Português', 'Literatura',
    'História', 'Geografia', 'Inglês', 'Espanhol', 'Filosofia', 'Sociologia',
    'Cálculo I', 'Cálculo II', 'Álgebra Linear', 'Estatística', 'Geometria Analítica',
    'Anatomia', 'Fisiologia', 'Farmacologia', 'Direito Civil', 'Direito Penal',
    'Administração', 'Contabilidade', 'Economia', 'Marketing', 'Psicologia',
    'Programação', 'Banco de Dados', 'Redes', 'Engenharia de Software',
    'Outra (especificar na descrição)'
  ];

  const cursosDisponiveis = [
    'Medicina', 'Enfermagem', 'Fisioterapia', 'Nutrição', 'Farmácia',
    'Direito', 'Administração', 'Ciências Contábeis', 'Economia',
    'Engenharia Civil', 'Engenharia Elétrica', 'Engenharia Mecânica',
    'Ciência da Computação', 'Sistemas de Informação', 'Análise e Desenvolvimento de Sistemas',
    'Psicologia', 'Pedagogia', 'Letras', 'História', 'Geografia',
    'Ensino Médio', 'Pré-vestibular', 'Concurso Público',
    'Outro'
  ];

  // Helpers Vídeo
  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const m = url?.match(regex);
    return m ? m[1] : null;
  };
  const getVimeoVideoId = (url) => {
    const m = url?.match(/(?:vimeo\.com\/)([0-9]+)/);
    return m ? m[1] : null;
  };
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return null;
    const y = getYouTubeVideoId(videoUrl);
    if (y) return `https://img.youtube.com/vi/${y}/hqdefault.jpg`;
    const v = getVimeoVideoId(videoUrl);
    if (v) return `https://vumbnail.com/${v}.jpg`;
    return null;
  };

  // Carregar dados iniciais
  useEffect(() => {
    const newTab = new URLSearchParams(location.search).get('tab') || 'dados';
    setAbaAtiva(newTab);
  }, [location.search]);

  useEffect(() => {
    if (abaAtiva === 'enviar') carregarCategorias();
    if (abaAtiva === 'envios') carregarMeusEnvios();
  }, [abaAtiva]);

  useEffect(() => {
    // Atualizar campos que dependem do user
    if (user) {
      setNovoMaterial(prev => ({
        ...prev,
        nome: user.name || '',
        email: user.email || ''
      }));
      setDadosPerfil(prev => ({
        ...prev,
        nome: user.name || '',
        email: user.email || '',
        telefone: user.telefone || '',
        bio: user.bio || ''
      }));
    }
  }, [user]);

  const carregarCategorias = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'categories'));
      const categoriasData = snapshot.docs.map((d) => ({
        id: d.id,
        name: d.data().name,
        subcategories: (d.data().subcategories || []).sort((a, b) => {
          const nameA = (a.name || a).toLowerCase();
          const nameB = (b.name || b).toLowerCase();
          return nameA.localeCompare(nameB);
        })
      }));

      // Ordenar categorias por nome
      categoriasData.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setCategorias(categoriasData);
    } catch (e) {
      console.error('Erro ao carregar categorias:', e);
    }
  };

  const carregarMeusEnvios = async () => {
    if (!user?.uid) return;
    setCarregandoEnvios(true);
    try {
      // Buscar tanto em student_materials quanto em perguntas_alunos
      const qMaterials = query(
        collection(db, 'student_materials'),
        where('authorUid', '==', user.uid)
      );
      const qPerguntas = query(
        collection(db, 'perguntas_alunos'),
        where('authorUid', '==', user.uid)
      );

      const [snapshotMaterials, snapshotPerguntas] = await Promise.all([
        getDocs(qMaterials),
        getDocs(qPerguntas)
      ]);

      const enviosMaterials = snapshotMaterials.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          fonte: 'materials', // identificar origem
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt)
        };
      });

      const enviosPerguntas = snapshotPerguntas.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          fonte: 'perguntas', // identificar origem
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : new Date(data.createdAt)
        };
      });

      const todosEnvios = [...enviosMaterials, ...enviosPerguntas];
      todosEnvios.sort((a, b) => b.createdAt - a.createdAt);
      setMeusEnvios(todosEnvios);
    } catch (e) {
      console.error('Erro ao carregar envios:', e);
    } finally {
      setCarregandoEnvios(false);
    }
  };

  const gerarAnosDisponiveis = () => {
    const anoAtual = new Date().getFullYear();
    const anos = [];
    for (let i = anoAtual; i >= anoAtual - 5; i--) anos.push(i);
    return anos;
  };

  const mudarAba = (novaAba) => {
    setAbaAtiva(novaAba);
    navigate(`/perfil?tab=${novaAba}`, { replace: true });
  };

  useEffect(() => {
    if (!toastObrigatorios) return undefined;

    const timeoutId = window.setTimeout(() => {
      setToastObrigatorios('');
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [toastObrigatorios]);

  // Handle para mudança de tipo de solicitação
  const handleTipoSolicitacaoChange = (tipo) => {
    setNovoMaterial(prev => ({
      ...prev,
      tipoSolicitacao: tipo,
      tipo: tipo === 'material' ? 'material' : 'duvida', // mapear para campos legados
      titulo: tipo === 'material' ? 'Material complementar' : 'Envio de questões'
    }));
    setToastObrigatorios('Preencha os campos obrigatórios marcados com * para informar categoria, disciplina e semana.');
  };

  const handleResetTipoSolicitacao = () => {
    setNovoMaterial(prev => ({
      ...prev,
      tipoSolicitacao: '',
      tipo: '',
      titulo: ''
    }));
    setToastObrigatorios('');
  };

  // Handle para mudança de preferências de formato
  const handleFormatoRespostaChange = (formato) => {
    setNovoMaterial(prev => {
      const formatosAtuais = prev.formatoResposta || [];
      const novaLista = formatosAtuais.includes(formato)
        ? formatosAtuais.filter(f => f !== formato)
        : [...formatosAtuais, formato];

      return {
        ...prev,
        formatoResposta: novaLista
      };
    });
  };

  /* ---------------------- PERFIL ---------------------- */
  const salvarDadosPerfil = async (e) => {
    e.preventDefault();
    setSalvandoDados(true);
    setMensagemDados('');

    try {
      let novaFotoURL = user?.photoURL;

      // Upload da foto se existir
      if (fotoFile) {
        const fotoRef = ref(storage, `profile_photos/${user.uid}`);
        const up = await uploadBytes(fotoRef, fotoFile);
        novaFotoURL = await getDownloadURL(up.ref);
      }

      // Atualizar perfil do Firebase Auth
      if (dadosPerfil.nome !== user?.name || novaFotoURL !== user?.photoURL) {
        await updateProfile(auth.currentUser, {
          displayName: dadosPerfil.nome,
          photoURL: novaFotoURL
        });
      }

      // Atualizar senha se fornecida
      if (dadosPerfil.novaSenha && dadosPerfil.novaSenha === dadosPerfil.confirmarSenha) {
        if (dadosPerfil.novaSenha.length < 6) {
          setMensagemDados('Senha deve ter pelo menos 6 caracteres.');
          setSalvandoDados(false);
          return;
        }
        await updatePassword(auth.currentUser, dadosPerfil.novaSenha);
      }

      // ✅ CORREÇÃO: Verificar se o documento existe antes de atualizar
      const userDocRef = doc(db, 'usuarios', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      const dadosParaSalvar = {
        name: dadosPerfil.nome,
        email: user.email,
        telefone: dadosPerfil.telefone,
        bio: dadosPerfil.bio,
        photoURL: novaFotoURL,
        updatedAt: new Date()
      };

      if (!userDocSnap.exists()) {
        // Se o documento não existe, cria um novo
        console.log('Documento não existe, criando novo...');
        await setDoc(userDocRef, {
          ...dadosParaSalvar,
          plano: user.plano || 'Free',
          createdAt: new Date(),
          uid: user.uid
        });
      } else {
        // Se existe, apenas atualiza
        console.log('Documento existe, atualizando...');
        await updateDoc(userDocRef, dadosParaSalvar);
      }

      // Atualizar contexto do usuário
      setUser((prev) => ({
        ...prev,
        name: dadosPerfil.nome,
        telefone: dadosPerfil.telefone,
        bio: dadosPerfil.bio,
        photoURL: novaFotoURL
      }));

      setMensagemDados('Dados salvos com sucesso!');
      setDadosPerfil((p) => ({ ...p, novaSenha: '', confirmarSenha: '' }));
      setFotoFile(null);

    } catch (e) {
      console.error('Erro ao salvar dados:', e);
      setMensagemDados('Erro ao salvar dados. Tente novamente.');
    } finally {
      setSalvandoDados(false);
    }
  };

  /* ---------------------- INPUT ARQUIVO (NOVO ENVIO) COM CROP ---------------------- */
  const onChangeArquivo = async (e) => {
    const file = e.target.files?.[0] || null;
    setNovoMaterial((prev) => ({ ...prev, arquivo: file }));

    // Se for PDF, abre modal de recorte
    if (file && /\.pdf$/i.test(file.name)) {
      try {
        const c = await renderPdfFirstPageToCanvas(file);
        setCropCanvas(c);
        setCropOpen(true);
      } catch (err) {
        console.error('Falha ao renderizar PDF:', err);
      }
    } else {
      // Se imagem, preview fica como a própria imagem
      setCropPreviewUrl('');
      cropBlobRef.current = null;
    }
  };

  /* ---------------------- ENVIAR MATERIAL/PERGUNTA ---------------------- */
  const enviarMaterial = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    if (!podeEnviarMaterial) {
      setMensagemEnvio('Você precisa estar logado para enviar materiais e perguntas.');
      return;
    }

    if (!novoMaterial.tipoSolicitacao) {
      setMensagemEnvio('Selecione se deseja enviar um material ou fazer uma pergunta.');
      return;
    }

    setEnviandoMaterial(true);
    setMensagemEnvio('');

    try {
      let arquivoURL = '';
      let materialType = 'TEXT';
      let coverImage = '';

      // Upload do arquivo se existir
      if (novoMaterial.arquivo) {
        const arquivoRef = ref(storage, `student_materials/${Date.now()}_${novoMaterial.arquivo.name}`);
        const upSnap = await uploadBytes(arquivoRef, novoMaterial.arquivo);
        arquivoURL = await getDownloadURL(upSnap.ref);

        const ext = novoMaterial.arquivo.name.split('.').pop().toLowerCase();
        if (ext === 'pdf') {
          materialType = 'PDF';
          // Se o usuário recortou, enviar thumbnail
          if (cropBlobRef.current) {
            const thumbRef = ref(storage, `student_thumbnails/${Date.now()}_${novoMaterial.arquivo.name.replace(/\.pdf$/i, '.jpg')}`);
            const tSnap = await uploadBytes(thumbRef, cropBlobRef.current);
            coverImage = await getDownloadURL(tSnap.ref);
          } else {
            coverImage = arquivoURL;
          }
        } else if (ext === 'png') {
          materialType = 'PNG';
          coverImage = arquivoURL;
        } else if (ext === 'jpg' || ext === 'jpeg') {
          materialType = 'JPG';
          coverImage = arquivoURL;
        } else {
          materialType = ext.toUpperCase();
          coverImage = arquivoURL;
        }
      }

      // Determinar coleção de destino baseado no tipo de solicitação
      const isQuestion = novoMaterial.tipoSolicitacao === 'questoes';
      const isMaterial = novoMaterial.tipoSolicitacao === 'material';

      const collectionName = isQuestion ? 'perguntas_alunos' : 'student_materials';
      const selectedWeek = String(novoMaterial.curso || '').trim();

      // Validar se pelo menos uma categoria está selecionada
      const validSelections = categorySelections.filter(sel => sel.category);
      if (validSelections.length === 0) {
        setMensagemEnvio('Selecione pelo menos uma categoria');
        setEnviandoMaterial(false);
        return;
      }

      const incompleteSelection = validSelections.find((sel) => !String(sel.subcategory || '').trim());
      if (incompleteSelection) {
        setMensagemEnvio('Selecione a subcategoria/disciplina para cada categoria escolhida.');
        setEnviandoMaterial(false);
        return;
      }

      if (!selectedWeek) {
        setMensagemEnvio('Selecione a semana do conteúdo antes de enviar.');
        setEnviandoMaterial(false);
        return;
      }

      // Preparar dados das categorias
      const categoriesData = validSelections.map(sel => {
        const cat = categorias.find(c => c.id === sel.category);
        return {
          categoryId: sel.category,
          categoryName: cat ? cat.name : '',
          subcategory: sel.subcategory || null
        };
      });

      // Para compatibilidade, usar primeira categoria como principal
      const primaryCategory = validSelections[0];
      const primaryDiscipline = String(primaryCategory?.subcategory || '').trim();

      const basePayload = {
        // Informações pessoais (já preenchidas)
        nome: novoMaterial.nome,
        email: novoMaterial.email,

        // Detalhes da solicitação
        tipoSolicitacao: novoMaterial.tipoSolicitacao,
        descricao: novoMaterial.descricao,

        // Contexto acadêmico
        semestre: novoMaterial.semestre,
        curso: selectedWeek,
        semana: selectedWeek,
        disciplina: primaryDiscipline,

        // Detalhes da questão
        arquivoURL,
        linkRelacionado: String(novoMaterial.linkRelacionado || '').trim(),
        urgencia: novoMaterial.urgencia,

        // Preferências
        formatoResposta: novoMaterial.formatoResposta,

        // Nova estrutura para múltiplas categorias
        categories: categoriesData,

        // Campos do sistema
        authorUid: user.uid,
        authorName: user.name,
        authorEmail: user.email,
        status: 'nova', // Para perguntas: nova/respondida/fechada | Para materiais: pendente/aprovado/rejeitado
        lida: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (isQuestion) {
        // Payload específico para perguntas_alunos
        const perguntaPayload = {
          ...basePayload,
          // Campos legados para compatibilidade
          titulo: novoMaterial.titulo,
          conteudo: novoMaterial.descricao, // usar descrição como conteudo
          tipo: novoMaterial.tipo,
          categoria: primaryCategory.category, // usar primeira categoria selecionada
          subcategoria: primaryCategory.subcategory || '', // usar primeira subcategoria selecionada
          materialType,
          coverImage,
          materialUrl: arquivoURL
        };

        await addDoc(collection(db, 'perguntas_alunos'), perguntaPayload);
        setMensagemEnvio('Pergunta enviada com sucesso! Aguarde a resposta dos professores.');
      } else {
        // Payload específico para student_materials  
        const materialPayload = {
          ...basePayload,
          status: 'pendente', // Para materiais
          // Campos legados para compatibilidade
          titulo: novoMaterial.titulo,
          tipo: 'material',
          tipoMaterial: 'arquivo',
          categoria: primaryCategory.category, // usar primeira categoria selecionada
          subcategoria: primaryCategory.subcategory || '', // usar primeira subcategoria selecionada
          conteudo: novoMaterial.descricao,
          videoUrl: '',
          materialType,
          materialUrl: arquivoURL,
          coverImage
        };

        await addDoc(collection(db, 'student_materials'), materialPayload);
        setMensagemEnvio('Material enviado com sucesso! Aguarde a análise dos professores.');
      }

      // Reset do formulário
      setNovoMaterial({
        nome: user?.name || '',
        email: user?.email || '',
        tipoSolicitacao: '',
        descricao: '',
        semestre: '',
        curso: '',
        arquivo: null,
        linkRelacionado: '',
        urgencia: 'normal',
        formatoResposta: [],
        titulo: '',
        tipo: '',
        tipoMaterial: 'arquivo',
        categoria: '',
        subcategoria: '',
        conteudo: '',
        videoUrl: '',
        materialType: 'TEXT'
      });
      setCategorySelections([{ category: '', subcategory: '' }]);
      setCropPreviewUrl('');
      cropBlobRef.current = null;
      carregarMeusEnvios();

    } catch (e) {
      console.error(e);
      setMensagemEnvio('Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setEnviandoMaterial(false);
    }
  };

  /* ---------------------- EDIÇÃO (mantive fluxo original) ---------------------- */
  const iniciarEdicao = (envio) => {
    setEditandoEnvio(envio.id);
    setDadosEdicao({
      titulo: envio.titulo,
      descricao: envio.descricao,
      tipo: envio.tipo,
      tipoMaterial: envio.tipoMaterial,
      categoria: envio.categoria,
      subcategoria: envio.subcategoria,
      conteudo: envio.conteudo,
      videoUrl: envio.videoUrl || '',
      arquivoURL: envio.arquivoURL || '',
      materialType: envio.materialType || 'TEXT',
      coverImage: envio.coverImage || ''
    });
    setArquivoEdicao(null);
    setMensagemEdicao('');
  };

  const cancelarEdicao = () => {
    setEditandoEnvio(null);
    setDadosEdicao({});
    setArquivoEdicao(null);
    setMensagemEdicao('');
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    setSalvandoEdicao(true);
    setMensagemEdicao('');

    try {
      let novoArquivoURL = dadosEdicao.arquivoURL;
      let materialType = dadosEdicao.materialType || 'TEXT';
      let coverImage = dadosEdicao.coverImage || '';

      // novo upload
      if (arquivoEdicao) {
        // tenta remover o antigo (se era arquivo)
        if (dadosEdicao.arquivoURL && dadosEdicao.tipoMaterial === 'arquivo') {
          try {
            const oldRef = ref(storage, dadosEdicao.arquivoURL);
            await deleteObject(oldRef);
          } catch (_) { }
        }
        const ar = ref(storage, `student_materials/${Date.now()}_${arquivoEdicao.name}`);
        const up = await uploadBytes(ar, arquivoEdicao);
        novoArquivoURL = await getDownloadURL(up.ref);

        const ext = arquivoEdicao.name.split('.').pop().toLowerCase();
        if (ext === 'pdf') {
          materialType = 'PDF';
          // gera thumbnail automático (simples) – para manter edição prática
          const can = await renderPdfFirstPageToCanvas(arquivoEdicao, 1200);
          const blob = await blobFromCanvas(can, 'image/jpeg', 0.9);
          const thumbRef = ref(storage, `student_thumbnails/${Date.now()}_${arquivoEdicao.name.replace(/\.pdf$/i, '.jpg')}`);
          const tSnap = await uploadBytes(thumbRef, blob);
          coverImage = await getDownloadURL(tSnap.ref);
        } else if (ext === 'png') { materialType = 'PNG'; coverImage = novoArquivoURL; }
        else if (ext === 'jpg' || ext === 'jpeg') { materialType = 'JPG'; coverImage = novoArquivoURL; }
        else { materialType = ext.toUpperCase(); coverImage = novoArquivoURL; }
      }

      // Determinar a coleção baseado na fonte do envio
      const envioAtual = meusEnvios.find(e => e.id === editandoEnvio);
      const collectionName = envioAtual?.fonte === 'perguntas' ? 'perguntas_alunos' : 'student_materials';

      await updateDoc(doc(db, collectionName, editandoEnvio), {
        ...dadosEdicao,
        arquivoURL: novoArquivoURL,
        materialType,
        coverImage,
        updatedAt: new Date()
      });

      setMensagemEdicao('Envio atualizado!');
      cancelarEdicao();
      carregarMeusEnvios();
    } catch (e) {
      console.error(e);
      setMensagemEdicao('Erro ao salvar edição. Tente novamente.');
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const deletarEnvio = async (envio) => {
    if (!window.confirm('Tem certeza que deseja deletar este envio?')) return;
    try {
      const collectionName = envio.fonte === 'perguntas' ? 'perguntas_alunos' : 'student_materials';
      await deleteDoc(doc(db, collectionName, envio.id));
      carregarMeusEnvios();
    } catch (e) {
      console.error(e);
      alert('Erro ao deletar envio.');
    }
  };

  // UI helpers
  const getStatusColor = (status) => {
    switch (status) {
      case 'aprovado': return '#16a34a';
      case 'rejeitado': return '#dc2626';
      case 'pendente': return '#f59e0b';
      case 'nova': return '#3b82f6';
      case 'respondida': return '#16a34a';
      case 'fechada': return '#6b7280';
      default: return '#6b7280';
    }
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case 'aprovado': case 'respondida': return <FaCheck />;
      case 'rejeitado': case 'fechada': return <FaTimes />;
      case 'pendente': case 'nova': return <FaClock />;
      default: return <FaQuestion />;
    }
  };
  const getTipoIcon = (tipo, tipoSolicitacao) => {
    if (tipoSolicitacao) {
      switch (tipoSolicitacao) {
        case 'questoes': return <FaQuestion />;
        case 'material': return <FaFile />;
        default: return <FaFile />;
      }
    }
    switch (tipo) {
      case 'duvida': return <FaQuestion />;
      case 'exercicio': return <FaCalculator />;
      case 'material': return <FaFile />;
      default: return <FaFile />;
    }
  };
  const getMaterialIcon = (tipoMaterial, materialType) => {
    if (tipoMaterial === 'video') return <FaVideo />;
    if (tipoMaterial === 'arquivo') {
      if (materialType === 'PDF') return <FaFilePdf />;
      if (['JPG', 'JPEG', 'PNG'].includes(materialType)) return <FaImage />;
      return <FaFile />;
    }
    return <FaFile />;
  };

  const enviosFiltrados = meusEnvios.filter((envio) => {
    const d = envio.createdAt;
    const ano = d.getFullYear();
    const mes = d.getMonth() + 1;
    const anoOk = ano === filtroAno;
    const mesOk = !filtroMes || mes === parseInt(filtroMes, 10);
    return anoOk && mesOk;
  });

  return (
    <div className="perfil-container">
      {/* Header */}
      <div className="perfil-header">
        <div className="perfil-background">
          <h1>Meu Perfil</h1>
        </div>

        <div className="perfil-info">
          <div className="perfil-avatar">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="Avatar" />
            ) : (
              <div className="perfil-avatar-placeholder">{firstLetter}</div>
            )}
          </div>

          <div className="perfil-nome-email">
            <h2>{user?.name}</h2>
            <p>{user?.email}</p>
            {user?.plano === 'Free' && <span className="perfil-plano">Gratuito</span>}
          </div>
        </div>

        {/* Tabs */}
        <div className="perfil-tabs">
          <div className={`perfil-tab ${abaAtiva === 'dados' ? 'ativa' : ''}`} onClick={() => mudarAba('dados')}>
            <FaUser /> Meus Dados
          </div>
          <div className={`perfil-tab ${abaAtiva === 'enviar' ? 'ativa' : ''}`} onClick={() => mudarAba('enviar')}>
            <FaUpload /> Fazer Pergunta/Enviar Material
          </div>
          <div className={`perfil-tab ${abaAtiva === 'envios' ? 'ativa' : ''}`} onClick={() => mudarAba('envios')}>
            <FaHistory /> Meus Envios
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="perfil-conteudo">
        {/* ABA: DADOS */}
        {abaAtiva === 'dados' && (
          <div className="dados-container">
            <h3>Informações Pessoais</h3>

            <form onSubmit={salvarDadosPerfil} className="dados-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    value={dadosPerfil.nome}
                    onChange={(e) => setDadosPerfil((prev) => ({ ...prev, nome: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>E-mail</label>
                  <input type="email" value={dadosPerfil.email} disabled className="input-disabled" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="tel"
                    value={dadosPerfil.telefone}
                    onChange={(e) => setDadosPerfil((prev) => ({ ...prev, telefone: e.target.value }))}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label>Foto de Perfil</label>
                  <input type="file" accept="image/*" onChange={(e) => setFotoFile(e.target.files[0])} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nova Senha</label>
                  <input
                    type="password"
                    value={dadosPerfil.novaSenha}
                    onChange={(e) => setDadosPerfil((prev) => ({ ...prev, novaSenha: e.target.value }))}
                    placeholder="Deixe em branco para manter a atual"
                  />
                </div>

                <div className="form-group">
                  <label>Confirmar Nova Senha</label>
                  <input
                    type="password"
                    value={dadosPerfil.confirmarSenha}
                    onChange={(e) => setDadosPerfil((prev) => ({ ...prev, confirmarSenha: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={dadosPerfil.bio}
                  onChange={(e) => setDadosPerfil((prev) => ({ ...prev, bio: e.target.value }))}
                  placeholder="Conte um pouco sobre você."
                  rows={4}
                />
              </div>

              {mensagemDados && (
                <div className={`mensagem ${mensagemDados.includes('Erro') ? 'erro' : 'sucesso'}`}>{mensagemDados}</div>
              )}

              <button type="submit" disabled={salvandoDados} className="btn-salvar">
                <FaSave /> {salvandoDados ? 'Salvando...' : 'Salvar Dados'}
              </button>
            </form>
          </div>
        )}

        {/* ABA: ENVIAR MATERIAL/PERGUNTA - FORMULÁRIO ATUALIZADO */}
        {abaAtiva === 'enviar' && (
          <div className="enviar-container">
            {toastObrigatorios && (
              <div className="toast-message toast-message-warning" role="status" aria-live="polite">
                <div className="toast-message-content">
                  <FaExclamationTriangle />
                  <span>{toastObrigatorios}</span>
                </div>
                <button
                  type="button"
                  className="toast-message-close"
                  onClick={() => setToastObrigatorios('')}
                  aria-label="Fechar aviso"
                >
                  <FaTimes />
                </button>
              </div>
            )}

            <h3>Fazer Pergunta ou Enviar Material</h3>
            <p className="form-description">
              Preencha o formulário abaixo para enviar sua dúvida, exercício ou material complementar.
              Nossa equipe analisará e responderá conforme a urgência selecionada.
            </p>

            {!podeEnviarMaterial && (
              <div className="mensagem erro">
                <p><strong>Login Necessário!</strong></p>
                <p>Você precisa estar logado para enviar perguntas e materiais.</p>
                <button
                  onClick={() => navigate('/login')}
                  className="btn-login"
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1.5rem',
                    background: '#209cd3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Fazer Login
                </button>
              </div>
            )}

            {!novoMaterial.tipoSolicitacao ? (
              <div className="envio-step">
                <h4>O que você deseja fazer?</h4>
                <p className="envio-step-subtitle">Escolha uma opção para continuar.</p>
                <div className="envio-opcoes">
                  <button
                    type="button"
                    className="envio-opcao envio-opcao-material"
                    onClick={() => handleTipoSolicitacaoChange('material')}
                    disabled={!podeEnviarMaterial}
                  >
                    <span className="envio-opcao-title"><FaFile /> Enviar Material</span>
                    <span className="envio-opcao-desc">Compartilhe PDFs, slides, resumos e conteúdos complementares.</span>
                  </button>
                  <button
                    type="button"
                    className="envio-opcao envio-opcao-pergunta"
                    onClick={() => handleTipoSolicitacaoChange('questoes')}
                    disabled={!podeEnviarMaterial}
                  >
                    <span className="envio-opcao-title"><FaQuestion /> Fazer Pergunta</span>
                    <span className="envio-opcao-desc">Envie sua dúvida ou exercício para os especialistas.</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="envio-step-header">
                  <span className="envio-step-label">Tipo selecionado:</span>
                  <span className={`envio-chip ${novoMaterial.tipoSolicitacao === 'material' ? 'material' : 'questoes'}`}>
                    {novoMaterial.tipoSolicitacao === 'material' ? 'Material' : 'Pergunta'}
                  </span>
                  <button
                    type="button"
                    className="envio-trocar"
                    onClick={handleResetTipoSolicitacao}
                    disabled={!podeEnviarMaterial}
                  >
                    Trocar
                  </button>
                </div>

                <form onSubmit={enviarMaterial} className="enviar-form" style={{ opacity: podeEnviarMaterial ? 1 : 0.5 }}>

              {/* SEÇÃO: INFORMAÇÕES PESSOAIS */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaUser /> Informações Pessoais
                </h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome Completo</label>
                    <input
                      type="text"
                      value={novoMaterial.nome}
                      disabled
                      className="input-disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label>E-mail</label>
                    <input
                      type="email"
                      value={novoMaterial.email}
                      disabled
                      className="input-disabled"
                    />
                  </div>
                </div>
              </div>

              {/* SEÇÃO: DETALHES DA SOLICITAÇÃO */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaQuestion /> Detalhes da Solicitação
                </h4>

                <div className="form-group">
                  <label>Categorias e Subcategorias *</label>
                  {categorySelections.map((selection, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'center',
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ flex: 1 }}>
                        <select
                          value={selection.category}
                          onChange={(e) => updateCategorySelection(index, 'category', e.target.value)}
                          required={index === 0}
                          disabled={!podeEnviarMaterial}
                          style={{ marginBottom: '0.5rem' }}
                        >
                          <option value="">Selecione uma categoria</option>
                          {categorias.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selection.category && (
                        <div style={{ flex: 1 }}>
                          <select
                            value={selection.subcategory}
                            onChange={(e) => updateCategorySelection(index, 'subcategory', e.target.value)}
                            required={Boolean(selection.category)}
                            disabled={!podeEnviarMaterial}
                          >
                            <option value="">Selecione uma subcategoria/disciplina</option>
                            {getSubcategoryOptions(selection.category).map((sub) => (
                              <option key={sub.name || sub} value={sub.name || sub}>
                                {sub.name || sub}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {categorySelections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCategorySelection(index)}
                          disabled={!podeEnviarMaterial}
                          style={{
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remover categoria"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addCategorySelection}
                    disabled={!podeEnviarMaterial}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: '#2ecc71',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#27ae60'}
                    onMouseOut={(e) => e.target.style.background = '#2ecc71'}
                  >
                    <FaPlus /> Adicionar mais categorias
                  </button>
                  <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                    Você pode adicionar múltiplas categorias para melhor organização do conteúdo.
                  </small>
                  <small className="form-help" title="Obrigatório informar a categoria e a subcategoria/disciplina para que a análise do material chegue corretamente identificada.">
                    Campos obrigatórios: categoria e subcategoria/disciplina.
                  </small>
                </div>

                {novoMaterial.tipoSolicitacao === 'questoes' && (
                  <div className="form-group">
                    <label>Descrição da pergunta/questão *</label>
                    <textarea
                      value={novoMaterial.descricao}
                      onChange={(e) => setNovoMaterial((prev) => ({ ...prev, descricao: e.target.value }))}
                      rows={4}
                      required
                      disabled={!podeEnviarMaterial}
                      placeholder="Descreva detalhadamente sua dúvida ou o exercício que precisa resolver..."
                    />
                  </div>
                )}
              </div>

              {/* SEÇÃO: CONTEXTO ACADÊMICO */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaGraduationCap /> Contexto Acadêmico
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>Semestre Atual</label>
                    <select
                      value={novoMaterial.semestre}
                      onChange={(e) => setNovoMaterial((prev) => ({ ...prev, semestre: e.target.value }))}
                      disabled={!podeEnviarMaterial}
                    >
                      <option value="">Selecione o semestre</option>
                      <option value="1">1º Semestre</option>
                      <option value="2">2º Semestre</option>
                      <option value="3">3º Semestre</option>
                      <option value="4">4º Semestre</option>
                      <option value="5">5º Semestre</option>
                      <option value="6">6º Semestre</option>
                      <option value="7">7º Semestre</option>
                      <option value="8">8º Semestre</option>
                      <option value="9">9º Semestre</option>
                      <option value="10">10º Semestre</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Semana *</label>
                    <select
                      value={novoMaterial.curso}
                      onChange={(e) => setNovoMaterial((prev) => ({ ...prev, curso: e.target.value }))}
                      required
                      disabled={!podeEnviarMaterial}
                    >
                      <option value="">Selecione a semana</option>
                      <option value="Semana 1">Semana 1</option>
                      <option value="Semana 2">Semana 2</option>
                      <option value="Semana 3">Semana 3</option>
                      <option value="Semana 4">Semana 4</option>
                      <option value="Semana 5">Semana 5</option>
                      <option value="Semana 6">Semana 6</option>
                      <option value="Semana 7">Semana 7</option>
                      <option value="Semana 8">Semana 8</option>
                    </select>
                    <small className="form-help" title="Obrigatório informar a semana para evitar envios sem referência acadêmica.">
                      Informe a semana obrigatoriamente.
                    </small>
                  </div>
                </div>
              </div>

              {/* SEÇÃO: DETALHES DA QUESTÃO */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaFile /> Detalhes da Questão
                </h4>

                <div className="form-group">
                  <label>Anexar arquivo (PDF, PNG, JPG)</label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={onChangeArquivo}
                    disabled={!podeEnviarMaterial}
                  />
                  <small className="form-help">
                    Anexe prints da questão, exercício ou material de apoio
                  </small>

                  {cropPreviewUrl && (
                    <div style={{ marginTop: 10 }}>
                      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Prévia selecionada:</p>
                      <img
                        src={cropPreviewUrl}
                        alt="Prévia recortada"
                        style={{ width: 240, height: 135, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }}
                      />
                    </div>
                  )}

                  {novoMaterial.arquivo && /\.pdf$/i.test(novoMaterial.arquivo.name) && (
                    <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
                      Abriremos um recorte da 1ª página antes de enviar. Você pode ajustar e confirmar.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Link relacionado</label>
                  <input
                    type="url"
                    value={novoMaterial.linkRelacionado}
                    onChange={(e) => setNovoMaterial((prev) => ({ ...prev, linkRelacionado: e.target.value }))}
                    placeholder="Cole aqui links do AVA, YouTube ou outros materiais"
                    disabled={!podeEnviarMaterial}
                  />
                  <small className="form-help">
                    <FaLink /> Cole links do AVA, YouTube ou outros materiais relacionados
                  </small>
                </div>

                <div className="form-group">
                  <label>Urgência</label>
                  <select
                    value={novoMaterial.urgencia}
                    onChange={(e) => setNovoMaterial((prev) => ({ ...prev, urgencia: e.target.value }))}
                    disabled={!podeEnviarMaterial}
                  >
                    <option value="normal">Normal (até 7 dias)</option>
                    <option value="urgente">Urgente (até 3 dias)</option>
                    <option value="muito_urgente">Muito urgente (até 24h)</option>
                  </select>
                </div>
              </div>

              {/* SEÇÃO: PREFERÊNCIAS */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaCog /> Preferências de Resposta
                </h4>

                <div className="form-group">
                  <label>Formato de resposta preferido (marque todos que deseja)</label>
                  <div className="checkbox-group">
                    {[
                      { value: 'videoaula', label: 'Videoaula explicativa' },
                      { value: 'material_escrito', label: 'Material escrito/PDF' },
                      { value: 'resumo', label: 'Resumo do conteúdo' },
                      { value: 'exercicio_resolvido', label: 'Exercício resolvido passo a passo' },
                      { value: 'apenas_compartilhando', label: 'Apenas compartilhando' }
                    ].map((formato) => (
                      <label key={formato.value} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={novoMaterial.formatoResposta.includes(formato.value)}
                          onChange={() => handleFormatoRespostaChange(formato.value)}
                          disabled={!podeEnviarMaterial}
                        />
                        {formato.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {mensagemEnvio && (
                <div className={`mensagem ${mensagemEnvio.includes('Erro') ? 'erro' : 'sucesso'}`} style={{ marginTop: 16 }}>
                  {mensagemEnvio}
                </div>
              )}

              <button type="submit" disabled={enviandoMaterial || !podeEnviarMaterial} className="btn-salvar">
                <FaPlus /> {enviandoMaterial ? 'Enviando...' : 'Enviar Solicitação'}
              </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* ABA: MEUS ENVIOS */}
        {abaAtiva === 'envios' && (
          <div className="envios-container">
            <div className="envios-header">
              <h3>Meus Envios ({enviosFiltrados.length})</h3>

              <div className="filtros">
                <div className="filtro-grupo">
                  <FaFilter />
                  <select value={filtroAno} onChange={(e) => setFiltroAno(parseInt(e.target.value, 10))}>
                    {gerarAnosDisponiveis().map((ano) => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ))}
                  </select>
                </div>

                <select value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}>
                  <option value="">Todos os meses</option>
                  {[
                    { valor: '1', nome: 'Janeiro' },
                    { valor: '2', nome: 'Fevereiro' },
                    { valor: '3', nome: 'Março' },
                    { valor: '4', nome: 'Abril' },
                    { valor: '5', nome: 'Maio' },
                    { valor: '6', nome: 'Junho' },
                    { valor: '7', nome: 'Julho' },
                    { valor: '8', nome: 'Agosto' },
                    { valor: '9', nome: 'Setembro' },
                    { valor: '10', nome: 'Outubro' },
                    { valor: '11', nome: 'Novembro' },
                    { valor: '12', nome: 'Dezembro' }
                  ].map((m) => (
                    <option key={m.valor} value={m.valor}>
                      {m.nome}
                    </option>
                  ))}
                </select>

                <button onClick={carregarMeusEnvios} className="btn-atualizar">
                  Atualizar
                </button>
              </div>
            </div>

            {carregandoEnvios ? (
              <div className="loading">
                <div className="spinner" />
                Carregando envios...
              </div>
            ) : enviosFiltrados.length === 0 ? (
              <div className="empty-state">
                <FaExclamationTriangle />
                <h4>Nenhum envio encontrado</h4>
                <p>Você ainda não enviou nenhuma pergunta ou material.</p>
                <button onClick={() => mudarAba('enviar')} className="btn-primeiro-envio">
                  Fazer Primeira Pergunta
                </button>
              </div>
            ) : (
              <div className="envios-lista">
                {enviosFiltrados.map((envio) => (
                  <div key={envio.id} className="envio-card">
                    {editandoEnvio === envio.id ? (
                      // Formulário de edição (mantido simples)
                      <form onSubmit={salvarEdicao} className="enviar-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>Título</label>
                            <input
                              type="text"
                              value={dadosEdicao.titulo || ''}
                              onChange={(e) => setDadosEdicao((prev) => ({ ...prev, titulo: e.target.value }))}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label>Tipo</label>
                            <select
                              value={dadosEdicao.tipo || 'duvida'}
                              onChange={(e) => setDadosEdicao((prev) => ({ ...prev, tipo: e.target.value }))}
                            >
                              <option value="duvida">Dúvida</option>
                              <option value="exercicio">Exercício</option>
                              <option value="material">Material</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Categoria</label>
                            <select
                              value={dadosEdicao.categoria || ''}
                              onChange={(e) =>
                                setDadosEdicao((prev) => ({ ...prev, categoria: e.target.value, subcategoria: '' }))
                              }
                            >
                              <option value="">Selecione uma categoria</option>
                              {categorias.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {dadosEdicao.categoria && (
                            <div className="form-group">
                              <label>Subcategoria</label>
                              <select
                                value={dadosEdicao.subcategoria || ''}
                                onChange={(e) => setDadosEdicao((prev) => ({ ...prev, subcategoria: e.target.value }))}
                              >
                                <option value="">Selecione uma subcategoria</option>
                                {categorias
                                  .find((cat) => cat.id === dadosEdicao.categoria)
                                  ?.subcategories.map((sub) => (
                                    <option key={sub.name || sub} value={sub.name || sub}>
                                      {sub.name || sub}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Descrição</label>
                          <textarea
                            value={dadosEdicao.descricao || ''}
                            onChange={(e) => setDadosEdicao((prev) => ({ ...prev, descricao: e.target.value }))}
                            rows={3}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Conteúdo/Pergunta</label>
                          <textarea
                            value={dadosEdicao.conteudo || ''}
                            onChange={(e) => setDadosEdicao((prev) => ({ ...prev, conteudo: e.target.value }))}
                            rows={6}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Novo Arquivo (PDF, PNG, JPG) - Opcional</label>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={(e) => setArquivoEdicao(e.target.files?.[0] || null)}
                          />
                          {dadosEdicao.arquivoURL && (
                            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                              Arquivo atual: {dadosEdicao.arquivoURL.split('/').pop().split('_').slice(1).join('_')}
                            </p>
                          )}
                        </div>

                        {mensagemEdicao && (
                          <div className={`mensagem ${mensagemEdicao.includes('Erro') ? 'erro' : 'sucesso'}`}>
                            {mensagemEdicao}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                          <button type="submit" disabled={salvandoEdicao} className="btn-salvar">
                            <FaSave /> {salvandoEdicao ? 'Salvando...' : 'Salvar'}
                          </button>

                          <button type="button" onClick={cancelarEdicao} className="btn-deletar">
                            <FaTimes /> Cancelar
                          </button>
                        </div>
                      </form>
                    ) : (
                      // Visualização normal
                      <div className="envio-header">
                        <div className="envio-info">
                          <div className="envio-titulo">
                            <div className="tipo-icon" style={{ color: getStatusColor(envio.status) }}>
                              {getTipoIcon(envio.tipo, envio.tipoSolicitacao)}
                            </div>
                            <h4>{envio.titulo || envio.descricao?.substring(0, 50) + '...'}</h4>
                            <div className="material-type-icon" style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                              {getMaterialIcon(envio.tipoMaterial, envio.materialType)}
                            </div>
                            <div
                              className="status-badge"
                              style={{
                                background: `${getStatusColor(envio.status)}20`,
                                color: getStatusColor(envio.status)
                              }}
                            >
                              {getStatusIcon(envio.status)}
                              {envio.status?.toUpperCase?.()}
                            </div>
                          </div>

                          {/* Informações específicas do novo formulário */}
                          {envio.tipoSolicitacao && (
                            <div className="envio-detalhes">
                              <div className="detalhes-row">
                                <span className="detalhe-label">Tipo:</span>
                                <span className="detalhe-valor">
                                  {envio.tipoSolicitacao === 'duvida' && 'Dúvida sobre conteúdo'}
                                  {envio.tipoSolicitacao === 'exercicio' && 'Resolução de exercício'}
                                  {envio.tipoSolicitacao === 'prova' && 'Prova resolvida'}
                                  {envio.tipoSolicitacao === 'material' && 'Material complementar'}
                                  {envio.tipoSolicitacao === 'outro' && 'Outro'}
                                  {envio.tipoSolicitacao === 'questoes' && 'Envio de questões'}
                                </span>
                              </div>

                              {envio.categoria && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Categoria:</span>
                                  <span className="detalhe-valor">
                                    {categorias.find(cat => cat.id === envio.categoria)?.name || envio.categoria}
                                  </span>
                                </div>
                              )}

                              {envio.subcategoria && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Subcategoria:</span>
                                  <span className="detalhe-valor">{envio.subcategoria}</span>
                                </div>
                              )}

                              {envio.curso && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Curso:</span>
                                  <span className="detalhe-valor">{envio.curso}</span>
                                </div>
                              )}

                              {envio.semestre && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Semestre:</span>
                                  <span className="detalhe-valor">{envio.semestre}º Semestre</span>
                                </div>
                              )}

                              {envio.urgencia && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Urgência:</span>
                                  <span className={`detalhe-valor urgencia-${envio.urgencia}`}>
                                    {envio.urgencia === 'normal' && 'Normal (até 7 dias)'}
                                    {envio.urgencia === 'urgente' && 'Urgente (até 3 dias)'}
                                    {envio.urgencia === 'muito_urgente' && 'Muito urgente (até 24h)'}
                                  </span>
                                </div>
                              )}

                              {envio.formatoResposta && envio.formatoResposta.length > 0 && (
                                <div className="detalhes-row">
                                  <span className="detalhe-label">Formato preferido:</span>
                                  <span className="detalhe-valor">
                                    {envio.formatoResposta.map(formato => {
                                      const labels = {
                                        'videoaula': 'Videoaula',
                                        'material_escrito': 'Material escrito',
                                        'resumo': 'Resumo',
                                        'exercicio_resolvido': 'Exercício resolvido',
                                        'apenas_compartilhando': 'Apenas compartilhando'
                                      };
                                      return labels[formato] || formato;
                                    }).join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          <p className="envio-descricao">{envio.descricao || envio.conteudo}</p>

                          {/* Link relacionado */}
                          {envio.linkRelacionado && (
                            <div style={{ marginBottom: '1rem' }}>
                              <a
                                href={envio.linkRelacionado}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.5rem 1rem',
                                  background: 'rgba(59, 130, 246, 0.1)',
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  fontWeight: '500'
                                }}
                              >
                                <FaLink />
                                Link relacionado
                              </a>
                            </div>
                          )}

                          {/* Link arquivo */}
                          {envio.arquivoURL && (
                            <div style={{ marginBottom: '1rem' }}>
                              <a
                                href={envio.arquivoURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.5rem 1rem',
                                  background: 'rgba(32, 156, 211, 0.1)',
                                  color: '#209cd3',
                                  textDecoration: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  fontWeight: '500'
                                }}
                              >
                                {getMaterialIcon('arquivo', envio.materialType)}
                                Ver Arquivo
                              </a>
                            </div>
                          )}

                          <div className="envio-meta">
                            <span>
                              <FaCalendarAlt style={{ marginRight: '0.25rem' }} />
                              Enviado em: {envio.createdAt.toLocaleDateString('pt-BR')}
                            </span>
                            {envio.fonte && (
                              <span>Tipo: {envio.fonte === 'perguntas' ? 'Pergunta' : 'Material'}</span>
                            )}
                            {envio.updatedAt && envio.updatedAt.toDate && (
                              <span>Editado em: {envio.updatedAt.toDate().toLocaleDateString('pt-BR')}</span>
                            )}
                          </div>
                        </div>

                        <div className="envio-acoes">
                          {(envio.status === 'pendente' || envio.status === 'nova') && (
                            <>
                              <button onClick={() => iniciarEdicao(envio)} className="btn-editar">
                                <FaEdit /> Editar
                              </button>

                              <button onClick={() => deletarEnvio(envio)} className="btn-deletar">
                                <FaTrash /> Deletar
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de recorte para PDF (envio novo) */}
      {cropOpen && cropCanvas && (
        <PdfCropperModal
          open={cropOpen}
          sourceCanvas={cropCanvas}
          onClose={() => {
            setCropOpen(false);
            // se cancelar, não salva recorte
          }}
          onConfirm={(blob, dataUrl) => {
            cropBlobRef.current = blob;
            setCropPreviewUrl(dataUrl);
            setCropOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default Perfil;
